/**
 * Lazysizes helpers for Kirby media snippets.
 */

export { bindFeaturedCoverFades, initFeaturedCoverMedia } from "./featured-cover-media";

export function configureLazySizes() {
  if (!window.lazySizes) {
    return;
  }

  window.lazySizes.cfg = window.lazySizes.cfg || {};
  window.lazySizes.cfg.loadMode = 1;
  window.lazySizes.cfg.expand = 280;
}

export function initLazyMedia(root = document) {
  const scope = root?.querySelector ? root : document;
  const container = scope === document ? document.getElementById("main") : scope;

  if (!container || !window.lazySizes?.loader) {
    return;
  }

  window.lazySizes.loader.checkElems(container);
}

/**
 * Force-load lazy media inside a container (e.g. carousel / slideshow slides
 * that are off-screen). Promotes data-src(set) immediately so the browser
 * starts fetching even when lazysizes viewport checks would skip the element.
 *
 * Must also unveil <source data-srcset> inside <picture> — otherwise matching
 * portrait/mobile sources keep the 1×1 gif placeholder forever and the real
 * cover image never loads (LQIP stays visible).
 */
export function preloadLazyMedia(root = document) {
  if (!root?.querySelectorAll) {
    return;
  }

  const fallbackWidth = Math.max(
    root.clientWidth || 0,
    root.getBoundingClientRect?.().width || 0,
    window.innerWidth || 0
  );

  // Picture sources first so the browser can pick a real candidate once the img unveils.
  root.querySelectorAll("source[data-srcset]").forEach((source) => {
    const dataSrcset = source.getAttribute("data-srcset");
    if (!dataSrcset) {
      return;
    }

    source.setAttribute("srcset", dataSrcset);
    source.removeAttribute("data-srcset");
  });

  root
    .querySelectorAll(
      "img.lazyload, img.lazyloading, video.lazyload, video.lazyloading, iframe.lazyload, iframe.lazyloading"
    )
    .forEach((el) => {
      if (el.getAttribute("data-sizes") === "auto") {
        const measured = Math.max(el.clientWidth || 0, fallbackWidth);
        if (measured > 0) {
          el.setAttribute("sizes", `${Math.round(measured)}px`);
          el.removeAttribute("data-sizes");
        } else {
          // Hidden / zero-width slides still need a usable sizes hint.
          el.setAttribute("sizes", "100vw");
          el.removeAttribute("data-sizes");
        }
      }

      const dataSrcset = el.getAttribute("data-srcset");
      if (dataSrcset) {
        el.setAttribute("srcset", dataSrcset);
        el.removeAttribute("data-srcset");
      }

      const dataSrc = el.getAttribute("data-src");
      if (dataSrc) {
        el.setAttribute("src", dataSrc);
        el.removeAttribute("data-src");
      }

      el.classList.remove("lazyload", "lazyloading");
      el.classList.add("lazyloaded");

      if (typeof el.decode === "function" && el.tagName === "IMG") {
        el.decode().catch(() => {});
      }
    });
}

/**
 * Preload every slide in project / plans / gallery carousels and the
 * homepage + about carousels so slide changes never show LQIP / blur-up.
 */
export function preloadCarouselMedia(root = document) {
  const scope = root?.querySelectorAll ? root : document;

  const carousels = [
    ...scope.querySelectorAll("[data-carousel]"),
    ...scope.querySelectorAll("[data-home-slideshow]"),
    ...scope.querySelectorAll(".c-about-carousel"),
  ];

  // Deduplicate if root itself matches a selector
  const unique = new Set(carousels);
  if (
    root?.matches &&
    (root.matches("[data-carousel]") ||
      root.matches("[data-home-slideshow]") ||
      root.matches(".c-about-carousel"))
  ) {
    unique.add(root);
  }

  unique.forEach((carousel) => {
    preloadLazyMedia(carousel);
  });
}
